import React, {useState} from 'react';
import classNames from 'classnames';
import {useInView} from 'react-intersection-observer';
import {AssetType, DetectionType} from '@taopix/taopix-core';
import {Shimmer, Theme, ThemeName} from '@taopix/taopix-design-system';
import {DragAndDropNames, KeyboardKeys} from '../../Enums';
import {StickerInterface} from '../../types';
import useImageLoader from '../../Hooks/Assets/useImageLoader';
import Drag from '../DragAndDrop/Drag';

export type StickerThumbnailProps = {
  columns: number;
  columnGap?: number;
  sticker: StickerInterface;
  onClick: (stickerAsset: StickerInterface) => void;
};

const StickerThumbnail = ({sticker, columns, columnGap = 5, onClick}: StickerThumbnailProps) => {
  const [loaded, setLoaded] = useState(false);
  const {ref, inView} = useInView({
    /* Optional options */
    threshold: 0.2,
  });
  const src = useImageLoader(sticker.asset, sticker.assetType, inView);

  const thumbnailContainerClassess = classNames([
    'relative',
    'before:block',
    'before:pt-[100%]',
    'cursor-pointer',
    'mt-[var(--gap)]',
    'ml-[var(--gap)]',
    // 'overflow-hidden',
    'w-[calc((100%_-_(var(--gap)_*_var(--columns)))_/_var(--columns))]',
  ]);

  const thumbnailWrapperClasses = classNames(
    'absolute',
    'flex',
    'items-center',
    'justify-center',
    'p-xs',
    'top-0',
    'left-0',
    'cursor-pointer',
    'w-full',
    'h-full',
    'rounded-[min(theme(borderRadius.themeCornerSize),5px)]',
    'hover:outline',
    'hover:outline-[3px]',
    'hover:outline-offset-[-3px]',
    'hover:outline-themeAccentColour'
  );

  const thumbnailClassess = ['object-contain m-auto max-w-full max-h-full'];
  const detectionType =
    AssetType.ScrapbookClipping === sticker.assetType
      ? DetectionType.ScrapbookClipping
      : DetectionType.ScrapBookImage;

  return (
    <li key={`li-${sticker.assetRef}`} className={thumbnailContainerClassess} ref={ref}>
      {!loaded && <Shimmer className={'absolute top-0'} />}

      {inView && (
        <Theme
          name={ThemeName.Section}
          allowBackground
          allowCorners={false}
          className={thumbnailWrapperClasses}
          tabIndex={0}
          onKeyDown={e => {
            if ([KeyboardKeys.ENTER, KeyboardKeys.SPACE].includes(e.key as KeyboardKeys)) {
              e.preventDefault();
              onClick(sticker);
            }
          }}
        >
          <Drag
            name={DragAndDropNames.PAGE}
            key={`sticker-${sticker.assetRef}`}
            detectionType={detectionType}
            item={sticker}
          >
            {src !== '' ? (
              <img
                className={thumbnailClassess.join(' ')}
                src={src}
                draggable={true}
                key={sticker.assetRef}
                alt={'sticker'}
                onLoad={e => {
                  if ((e.target as HTMLImageElement).classList.contains('hidden')) {
                    (e.target as HTMLImageElement).classList.remove('hidden');
                  }
                  setLoaded(true);
                }}
                onError={e => {
                  (e.target as HTMLImageElement).classList.add('hidden');
                  // console.error(e);
                }}
                onClick={() => onClick(sticker)}
              />
            ) : (
              <></>
            )}
          </Drag>
        </Theme>
      )}
    </li>
  );
};
export default StickerThumbnail;
